import { range } from 'lodash'
import React, { useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import RecordingAudio, { RecordingAudioVariantType } from './RecordingAudio'
import RecordingAudioBackdrop from './RecordingAudioBackdrop'
import OpacityAnimation from '@/components/animations/OpacityAnimation.tsx'
import { Column, Flex, Text } from '@/components/core'
import { classNameBuilder } from '@/helpers'

interface RecordingAudioProps {
  variant: RecordingAudioVariantType
  onRecordingEnd?: (audioBlob: Blob, seconds: number) => void
  onTooltipConfirm?: (audioBlob: Blob, seconds: number) => void
  pending?: boolean
  autoRecord?: boolean
  timerInSeconds: number
  waveHeight?: number
  waveScalling?: number
  showRecordingTimeRecommendationTooltip?: boolean
}

const Container = styled.div`
  @keyframes goIn {
    0% {
      transform: translate(-50%, -50%) rotate(180deg);
    }
    30% {
      transform: translate(-50%, -50%) rotate(-20deg);
    }

    60% {
      transform: translate(-50%, -50%) rotate(10deg);
    }

    90%,
    100% {
      transform: translate(-50%, -50%) rotate(0deg);
    }
  }

  @keyframes goOut {
    0%,
    30% {
      transform: translate(-50%, -50%) rotate(0deg);
    }

    60% {
      transform: translate(-50%, -50%) rotate(20deg);
    }

    100% {
      transform: translate(-50%, -50%) rotate(-180deg);
    }
  }

  @keyframes hide {
    0% {
      transform: translate(-50%, -50%) scale(1);
    }

    100% {
      transform: translate(-50%, -50%) scale(0);
    }
  }

  @keyframes show {
    0% {
      transform: translate(-50%, -50%) scale(0);
    }

    80% {
      transform: translate(-50%, -50%) scale(1.4);
    }

    100% {
      transform: translate(-50%, -50%) scale(1);
    }
  }

  position: relative;

  .counter {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    &.hide {
      transform: translate(-50%, -50%) scale(0);
      animation: hide 0.2s ease-out;
    }
    .nums {
      position: relative;
      overflow: hidden;
      width: 250px;
      height: 40px;
      span {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%) rotate(180deg);
        transform-origin: bottom center;
        &.in {
          transform: translate(-50%, -50%) rotate(0deg);
          animation: goIn 0.5s ease-in-out;
        }
        &.out {
          animation: goOut 0.5s ease-in-out;
        }
      }
    }
  }

  .final {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    &.show {
      transform: translate(-50%, -50%) scale(1);
      animation: show 0.3s ease-in;
    }
  }
`

function PendingTimerAnimatedCounter({ seconds }: { seconds: number }) {
  const numbers = useMemo(() => range(1, seconds + 1).reverse(), [seconds])

  const handleRef = useCallback((ref: HTMLDivElement | null) => {
    if (ref) {
      const nums = ref.querySelectorAll<HTMLSpanElement>('.nums span')
      const counter = ref.querySelector<HTMLDivElement>('.counter')
      const finalMessage = ref.querySelector<HTMLDivElement>('.final')

      nums.forEach((num, index) => {
        const penultimate = nums.length - 1
        num.addEventListener('animationend', (e) => {
          if (e.animationName === 'goIn' && index !== penultimate) {
            num.classList.remove('in')
            num.classList.add('out')
          } else if (e.animationName === 'goOut' && num.nextElementSibling) {
            num.nextElementSibling.classList.add('in')
          } else {
            counter?.classList.add('hide')
            finalMessage?.classList.add('show')
          }
        })
      })
    }
  }, [])

  return (
    <Container ref={handleRef}>
      <div className="counter">
        <div className="nums">
          {numbers.map((number, index) => (
            <Text
              key={number}
              color="none"
              size="3xl"
              fontWeight="bold"
              className={`text-electric-indigo-900 ${!index ? 'in' : ''}`}
            >
              {number}
            </Text>
          ))}
        </div>
        <Text
          color="none"
          size="sm"
          fontWeight="bold"
          className="text-electric-indigo-900"
        >
          GET READY TO SPEAK
        </Text>
      </div>
      <div className="final">
        <Text
          color="none"
          size="3xl"
          fontWeight="bold"
          className="text-electric-indigo-900"
        >
          GO
        </Text>
      </div>
    </Container>
  )
}

const RecordingAudioWithTimer: React.FC<RecordingAudioProps> = ({
  timerInSeconds,
  ...props
}) => {
  const [pendingTimer, setPendingTimer] = React.useState(true)

  useEffect(() => {
    const timer = setTimeout(
      () => {
        setPendingTimer(false)
      },
      (timerInSeconds + 0.5) * 1000
    )

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [])

  return (
    <>
      {pendingTimer && (
        <OpacityAnimation>
          <RecordingAudioBackdrop />
        </OpacityAnimation>
      )}
      <Column className="w-full items-center space-y-6">
        <Flex
          className={classNameBuilder(
            'h-24 w-full max-w-[532px] flex-row items-center justify-center rounded-full bg-white p-4',
            pendingTimer ? 'z-20' : ''
          )}
        >
          {pendingTimer && (
            <OpacityAnimation>
              <PendingTimerAnimatedCounter seconds={timerInSeconds} />
            </OpacityAnimation>
          )}
          {!pendingTimer && <RecordingAudio autoTurnOff {...props} />}
        </Flex>
      </Column>
    </>
  )
}

export default RecordingAudioWithTimer
